import { useEffect } from 'react';

import { FrontendSharedEvents } from 'shared/client/consts/frontendSharedEvents';
import { serializeError } from 'shared/client/utils/serializeError';
import { logFrontendEventApi } from 'shared/queries/logFrontendEventApi';

import { installResizeObserverDiagnostics, withResizeObserverDiagnostics } from './resizeObserverDiagnostics';

// At import, before the first render, so the Studio's observers are created through the diagnosed constructor.
installResizeObserverDiagnostics();

/**
 * Global error tracker for unhandled errors and promise rejections
 *
 * Logs critical errors that escape component boundaries without showing a fallback UI.
 * Helps identify and fix issues blocking students.
 */
export function GlobalErrorTracker() {
  useEffect(() => {
    // Track unhandled errors
    const handleError = (event: ErrorEvent) => {
      void logFrontendEventApi({
        eventType: FrontendSharedEvents.error.unhandledError,
        serializedError: serializeError(event.error || withResizeObserverDiagnostics(new Error(event.message))),
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        url: window.location.href
      });
    };

    // Track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      void logFrontendEventApi({
        eventType: FrontendSharedEvents.error.unhandledError,
        serializedError: serializeError(event.reason instanceof Error ? event.reason : new Error(String(event.reason))),
        reason: String(event.reason),
        url: window.location.href
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return null;
}
